'use client'

import React, { useState, useEffect, useMemo } from 'react'
import { useRouter } from 'next/navigation'
import { useSearchParams } from 'next/navigation'
import LandingPageContainer from '@/src/components/commons/generics/LandingPageContainer/LandingPageContainer'
import Footer from '@/src/components/commons/Footer/Footer'
import { fetchAdvisoryDetailData, mapAdvisoryDetail } from './utils'
import { AdvisoryDetailClientProps, RawGraphQLData, ErrorResponse } from './Advisory.interface'
import Image from 'next/image'
import DOMPurify from 'isomorphic-dompurify'
import parse from 'html-react-parser'
import SocialShareButtons from '@/src/components/commons/generics/SocialShareButtons/SocialShareButtons'
import { getAdvisoryCanonicalOrigin } from '@/src/utils/publicSiteUrl'
import styles from './AdvisoryDetail.module.scss'

import ShortBackArrowWhite from '@/public/assets/images/icon-short-back-arrow-white.svg'

// =========================================================================
// DOMPURIFY CONFIGURATION (Runs once on client mount)
// =========================================================================

// <a> tags in content from graphQL sometimes don't include target_blank, this will intercept and add that if link points externally
const setupLinkMutator = (hostToCheck: string) => {
  // Check if the hook has already been added globally
  if (DOMPurify.isConfigured || DOMPurify._is_custom_config_set) return

  // Regex to check if a URL is external to the specified host (e.g., zscaler.com)
  const isExternal = (href: string) => {
    try {
      const urlObj = new URL(href)
      // Check for explicit protocol AND check if the hostname does NOT contain the hostToCheck
      return urlObj.protocol.startsWith('http') && !urlObj.hostname.includes(hostToCheck)
    } catch (e) {
      console.log(e)
      return false // Ignore invalid URLs
    }
  }

  // Add hook to mutate anchor tags after they are sanitized
  DOMPurify.addHook('afterSanitizeAttributes', function (node) {
    if (node.tagName === 'A' && node.hasAttribute('href')) {
      const href = node.getAttribute('href') || ''

      if (isExternal(href)) {
        // Enforce opening in a new tab
        node.setAttribute('target', '_blank')
        // CRITICAL SECURITY FIX: Prevents reverse tabnabbing attacks
        node.setAttribute('rel', 'noopener noreferrer')
      }
    }
  })

  DOMPurify._is_custom_config_set = true
}

// =========================================================================
// MAIN CLIENT COMPONENT
// =========================================================================
const AdvisoryDetailClient: React.FC<AdvisoryDetailClientProps> = ({ advisorySlug }) => {
  const [data, setData] = useState<unknown>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const searchParams = useSearchParams()
  const router = useRouter()

  const sharePageUrl = useMemo(
    () => `${getAdvisoryCanonicalOrigin()}/security-advisories/${advisorySlug}`,
    [advisorySlug],
  )

  function isRawData(obj: unknown): obj is RawGraphQLData {
    return (
      typeof obj === 'object' &&
      obj !== null &&
      'data' in obj &&
      typeof (obj as RawGraphQLData).data === 'object' &&
      (obj as RawGraphQLData).data !== null &&
      'advisoriesGraphql' in (obj as RawGraphQLData).data
    )
  }

  // Type guard to check if the object has an 'error' property
  function hasErrorProperty(obj: unknown): obj is ErrorResponse {
    return (
      typeof obj === 'object' &&
      obj !== null &&
      'error' in obj &&
      typeof (obj as ErrorResponse).error === 'string'
    )
  }

  // RUN HOOK SETUP ON MOUNT
  useEffect(() => {
    // Set the link mutator to run once on mount, checking against the expected domain.
    setupLinkMutator('zscaler.com')
  }, [])

  const handleReturnToSearch = () => {
    router.back()
  }

  // Map the fetched data
  const advisoryDetails = useMemo(() => {
    // Use the type guard to check 'data'
    if (isRawData(data) && data.data.advisoriesGraphql.entities.length > 0) {
      return mapAdvisoryDetail(data.data.advisoriesGraphql.entities[0])
    }
    return null
  }, [data])

  // Fetch Data
  useEffect(() => {
    if (!advisorySlug) {
      return
    }

    setIsLoading(true)
    setError(null)

    const fetchData = async () => {
      try {
        const result: unknown = await fetchAdvisoryDetailData(advisorySlug)

        // Use type guards to check 'result'
        if (isRawData(result)) {
          setData(result)
        } else if (hasErrorProperty(result)) {
          setError(result.error)
        } else {
          setError('Advisory details not found in API response.')
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch data.')
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [advisorySlug])

  // Return Link Construction (Preserves Query Params)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const returnHref = useMemo(() => {
    const queryString = searchParams.toString()
    let href = '/security-advisories'
    if (queryString) {
      href = `${href}?${queryString}`
    }
    return href
  }, [searchParams])

  const summaryNodes = useMemo(() => {
    if (!advisoryDetails) return null
    return parse(DOMPurify.sanitize(advisoryDetails.summary))
  }, [advisoryDetails])

  const contentNodes = useMemo(() => {
    if (!advisoryDetails) return null
    return parse(DOMPurify.sanitize(advisoryDetails.contentHtml))
  }, [advisoryDetails])

  if (isLoading) {
    return (
      <div className={styles.advisoryDetailContainer}>
        <div className={`${styles.loading} text-white text-center py-[50px]`}>
          Loading details from API Proxy...
        </div>
      </div>
    )
  }

  if (error || !advisoryDetails) {
    return (
      <div className={styles.advisoryDetailContainer}>
        <div className={`detailTopNav pb-5 md:pb-[30px] font-gt-haptik`}>
          {/* Return to Search Results Link (Preserves query strings) */}
          <button
            onClick={handleReturnToSearch}
            className={`btnReturnToResults flex font-gt-haptik text-[16px] font-medium`}
          >
            <Image
              src={ShortBackArrowWhite.src}
              alt="Previous Result Page"
              width={16}
              height={16}
              className={`inline-block! align-middle backArrow mr-2.5`}
            />
            Return to search results
          </button>
        </div>
        <div className={styles.error}>{error || 'Advisory details not found.'}</div>
      </div>
    )
  }

  const advisory = advisoryDetails

  const formattedDate = advisory.publishDate.toUpperCase()

  return (
    <>
      <LandingPageContainer className={styles.pageWrapper}>
        <div className={styles.advisoryDetailContainer}>
          <div className={`detailTopNav pb-5 md:pb-[30px] font-gt-haptik`}>
            {/* Return to Search Results Link (Preserves query strings) */}
            <button
              onClick={handleReturnToSearch}
              className={`btnReturnToResults flex font-gt-haptik text-[16px] font-medium`}
            >
              <Image
                src={ShortBackArrowWhite.src}
                alt="Previous Result Page"
                width={16}
                height={16}
                className={`inline-block! align-middle backArrow mr-2.5`}
              />
              Return to search results
            </button>
          </div>
          {error && (
            <div
              className={`${styles.advisoryDetailHeader} font-gt-haptik font-normal pt-5 md:pt-[30px]`}
            >
              Error: {error}
            </div>
          )}
          <div
            className={`${styles.advisoryDetailHeader} font-gt-haptik font-normal pt-5 md:pt-[30px]`}
          >
            <div
              className={`${styles.dateSocialRow} flex flex-col md:flex-row justify-between gap-3 md:gap-0 mb-[30px] md:mb-0`}
            >
              <div className={`text-[12px] font-medium text-[#66748f] uppercase tracking-[2.4px]`}>
                {/* Image component for the icon (omitted for clean render) */}
                <span className={styles.securityAdvisoryLabel}>SECURITY ADVISORY</span>
                <span className={styles.publishDate}> — {formattedDate}</span>
              </div>
              <SocialShareButtons
                title={advisory.title}
                url={sharePageUrl}
                className={styles.socialShareButtons}
              />
            </div>

            <h1 className={`text-[22px] font-medium mb-2.5`}>{advisory.title}</h1>

            {/* Rich summary from CMS GraphQL: DOMPurify.sanitize + html-react-parser (React children, not raw HTML assignment). */}
            <div className={`${styles.advisorySummary} text-[16px] text-white opacity-[70] mb-[30px] pb-[30px] border-b-[#3E4752] border-b`}>
              {summaryNodes}
            </div>
          </div>
          {/* Full body from CMS GraphQL; same sanitization + html-react-parser as summary. */}
          <div className={styles.advisoryContent}>{contentNodes}</div>
        </div>
      </LandingPageContainer>
      <Footer />
    </>
  )
}

export default AdvisoryDetailClient
